import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import ButtonItem from '@atlaskit/menu/button-item';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	DROPDOWN,
	FireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { ModalEntryPointTrigger } from './ModalEntryPointTrigger.tsx';

const button = defineMessage({
	id: 'polaris-component-view-template-library.button',
	defaultMessage: 'Templates',
	description: 'Dropdown option to open View Templates Library',
});

const iconLabel = defineMessage({
	id: 'polaris-component-view-template-library.iconLabel',
	defaultMessage: 'Templates',
	description: 'Label for the icon of the button',
});

const lozenge = defineMessage({
	id: 'polaris-component-view-template-library.lozenge',
	defaultMessage: 'New',
	description: 'Label for the lozenge of the button',
});

type ViewTemplatesButtonProps = {
	onClick?: () => void;
	projectId: string;
	projectKey: string;
	containerId?: string;
};

export const ViewTemplatesButton = ({
	onClick,
	projectId,
	projectKey,
	containerId,
}: ViewTemplatesButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'viewTemplateLibrary',
		);
		onClick?.();
	}, [createAnalyticsEvent, onClick]);

	return (
		<JSErrorBoundary
			id="viewTemplateLibrary"
			packageName="jiraViewTemplateLibraryButton"
			teamName="jpd-juno"
			fallback="unmount"
		>
			<ContextualAnalyticsData sourceName="viewTemplateLibraryItem" sourceType={DROPDOWN}>
				<ModalEntryPointTrigger
					projectId={projectId}
					projectKey={projectKey}
					containerId={containerId}
				>
					{({ ref }) => (
						<ButtonItem
							iconBefore={<TemplateIcon label={formatMessage(iconLabel)} />}
							onClick={handleClick}
							ref={ref}
						>
							{fg('jpd_tree-view-standard-upgrade') ? (
								formatMessage(button)
							) : (
								<Inline alignBlock="center" spread="space-between" space="space.100">
									<Box>{formatMessage(button)}</Box>
									<Lozenge
										appearance={
											fg('platform-dst-lozenge-tag-badge-visual-uplifts') ? 'discovery' : 'new'
										}
									>
										{formatMessage(lozenge)}
									</Lozenge>
								</Inline>
							)}
						</ButtonItem>
					)}
				</ModalEntryPointTrigger>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
};
